/**
 * 容园 X87S 官方驱动灯效：展示顺序与文案与原厂客户端一致
 * effectId 仍走协议 LED Param Table；仅 UI 排序/命名与协议 ID 序不同。
 */

export type RongyuanProtocolLightEffect = {
  effectId: number;
  displayName: string;
  uiEnabled: boolean;
};

/** 协议全表（按 effectId，供 SDK / 能力查询） */
export const RONGYUAN_PROTOCOL_LIGHT_EFFECTS: readonly RongyuanProtocolLightEffect[] =
  [
    { effectId: 0x01, displayName: "常亮", uiEnabled: true },
    { effectId: 0x02, displayName: "动态呼吸", uiEnabled: true },
    { effectId: 0x03, displayName: "光谱循环", uiEnabled: true },
    { effectId: 0x04, displayName: "随波逐流", uiEnabled: true },
    { effectId: 0x05, displayName: "碧波荡漾", uiEnabled: true },
    { effectId: 0x06, displayName: "繁星点点", uiEnabled: true },
    { effectId: 0x07, displayName: "川流不息", uiEnabled: true },
    { effectId: 0x08, displayName: "如影随形", uiEnabled: true },
    { effectId: 0x09, displayName: "重峦叠嶂", uiEnabled: true },
    { effectId: 0x0a, displayName: "正弦波", uiEnabled: true },
    { effectId: 0x0b, displayName: "彩泉涌动", uiEnabled: true },
    /** 公版抓包：百花争艳 → SET_LED mode=0x0C (Line Wave) */
    { effectId: 0x0c, displayName: "百花争艳", uiEnabled: true },
    /** 协议 0x0D User picture；仅驱动「自定义」0x80 写入，不在 X87S 公版常规灯效列表 */
    { effectId: 0x0d, displayName: "用户图案", uiEnabled: false },
    { effectId: 0x0e, displayName: "一石二鸟", uiEnabled: true },
    { effectId: 0x0f, displayName: "峰回路转", uiEnabled: true },
    { effectId: 0x10, displayName: "多彩纵横", uiEnabled: true },
    { effectId: 0x11, displayName: "漫天飞雪", uiEnabled: true },
    { effectId: 0x12, displayName: "流星", uiEnabled: true },
    /** 公版抓包：踏雪无痕 → SET_LED mode=0x13 (协议 PressAction Off) */
    { effectId: 0x13, displayName: "踏雪无痕", uiEnabled: true },
    { effectId: 0x14, displayName: "音乐律动", uiEnabled: false },
    { effectId: 0x15, displayName: "Windows特效", uiEnabled: false },
    { effectId: 0x16, displayName: "音乐律动2", uiEnabled: false },
    { effectId: 0x17, displayName: "列车", uiEnabled: true },
    { effectId: 0x18, displayName: "烟花", uiEnabled: false },
    { effectId: 0x19, displayName: "GIF动画", uiEnabled: false },
  ];

const PROTOCOL_EFFECT_BY_ID = new Map(
  RONGYUAN_PROTOCOL_LIGHT_EFFECTS.map((item) => [item.effectId, item])
);

/**
 * 官方客户端背光灯效按钮顺序（21 项，不含音乐律动/烟花）
 * @see 容园 X87S 原厂灯光页
 */
export const RONGYUAN_UI_LIGHT_EFFECT_ORDER: readonly number[] = [
  0x04, // 随波逐流
  0x05, // 碧波荡漾
  0x06, // 繁星点点
  0x07, // 川流不息
  0x08, // 如影随形
  0x09, // 重峦叠嶂
  0x0a, // 正弦波
  0x0b, // 彩泉涌动
  0x0c, // 百花争艳（公版 0x0C）
  0x13, // 踏雪无痕（公版 0x13）
  0x0e, // 一石二鸟
  0x0f, // 峰回路转
  0x10, // 多彩纵横
  0x11, // 漫天飞雪
  0x12, // 流星
  0x01, // 常亮
  0x02, // 动态呼吸
  0x03, // 光谱循环
  0x17, // 列车
];

/** 协议 0x0D User picture；驱动「自定义」0x80 写设备时用此 mode（公版常规列表不展示） */
export const RONGYUAN_PROTOCOL_USER_PICTURE_MODE = 0x0d;

/** 公版「百花争艳」→ SET_LED mode=0x0C */
export const RONGYUAN_OFFICIAL_BAIHUA_EFFECT_MODE = 0x0c;

/** 公版「踏雪无痕」→ SET_LED mode=0x13 */
export const RONGYUAN_OFFICIAL_TAXUE_EFFECT_MODE = 0x13;

/** 驱动 UI：逐键自定义（写设备时映射为协议 0x0d） */
export const RONGYUAN_UI_CUSTOM_LIGHT_MODE = 0x80;

/** 协议 FEA_CMD_GET_SLEDPARAM / SET_SLEDPARAM（灯带/侧灯） */
export const RONGYUAN_PROTOCOL_GET_SLED_PARAM = 0x88;
export const RONGYUAN_PROTOCOL_SET_SLED_PARAM = 0x08;

/** X87S 虚拟灯带颗数（与 getDecorate1Custom 回显一致） */
export const RONGYUAN_DECORATE_STRIP_LED_COUNT = 8;

/** 官方驱动 SET_SLEDPARAM 炫彩/AP 默认色（抓包 fa ff fa） */
export const RONGYUAN_DECORATE_AP_RGB = {
  R: 0xfa,
  G: 0xff,
  B: 0xfa,
} as const;

/** 灯带 UI「关闭」按钮值（写设备时映射为协议 mode=0x00） */
export const RONGYUAN_DECORATE_OFF_UI_MODE = 9999;

/**
 * X87S 官方灯带 Tab 灯效（顺序与文案与原厂客户端一致）
 * 抓包：0x01 恒亮 / 0x02 呼吸 / 0x03 霓虹 / 0x04 光波 / 0x00 关闭
 * 「跑马」公版抓包 mode=0x05（Ripple）
 */
export const RONGYUAN_DECORATE_UI_EFFECTS: readonly {
  effectId: number;
  displayName: string;
  uiValue?: number;
}[] = [
  {
    effectId: 0x00,
    displayName: "关闭",
    uiValue: RONGYUAN_DECORATE_OFF_UI_MODE,
  },
  { effectId: 0x01, displayName: "恒亮" },
  { effectId: 0x02, displayName: "呼吸" },
  { effectId: 0x03, displayName: "霓虹" },
  { effectId: 0x04, displayName: "光波" },
  { effectId: 0x05, displayName: "跑马" },
];

/** 灯带 Tab 按钮（官方 6 项：关闭 / 恒亮 / 呼吸 / 霓虹 / 光波 / 跑马） */
export function supportsRongyuanDecorate1Lighting(
  keyboardType?: string
): boolean {
  return (
    String(keyboardType ?? "")
      .trim()
      .toLowerCase() === "rongyuan87"
  );
}

/** 社区/灯光页：X87S 等灯带 Tab 展示虚拟灯带（非虚拟键盘） */
export function shouldShowRongyuanLightStripVisualPreview(meta: {
  solutionProvider?: string;
  keyboardType?: string;
}): boolean {
  if (
    String(meta.solutionProvider ?? "")
      .trim()
      .toLowerCase() !== "rongyuan"
  ) {
    return false;
  }
  return supportsRongyuanDecorate1Lighting(meta.keyboardType);
}

/** 板载 decorate1Base → 社区灯效按钮 UI mode */
export function resolveRongyuanDecoratePreviewModeState(
  base: Record<string, unknown> | undefined
): { mode: number; lightOpen: boolean } {
  if (!base) {
    return {
      mode: RONGYUAN_DECORATE_OFF_UI_MODE,
      lightOpen: false,
    };
  }
  const nested = base.data as Record<string, unknown> | undefined;
  const rawMode = nested?.mode ?? base.mode;
  const mode = mapRongyuanDecorateDeviceModeToUiMode(rawMode);
  const lightOpen = !isRongyuanDecorateOffUiMode(mode);
  return { mode, lightOpen };
}

/** 容园 SDK 灯区 id */
export function resolveRongyuanSdkLightingArea(
  area?: string
): "Keyboard" | "Decorate1" {
  const value = String(area ?? "Keyboard").trim();
  return value === "Decorate1" ? "Decorate1" : "Keyboard";
}

/** 容园自定义灯 UI 矩阵（6×21，与 rongyuanLightingGrid 一致） */
export const RONGYUAN_CUSTOM_LIGHT_MATRIX = {
  rows: 6,
  cols: 21,
} as const;

export type RongyuanCustomLightCell = {
  R: number;
  G: number;
  B: number;
  isCustom: boolean;
};

export function createEmptyRongyuanCustomLightMatrix(): RongyuanCustomLightCell[][] {
  const { rows, cols } = RONGYUAN_CUSTOM_LIGHT_MATRIX;
  return Array.from({ length: rows }, () =>
    Array.from({ length: cols }, () => ({
      R: 0,
      G: 0,
      B: 0,
      isCustom: false,
    }))
  );
}

export function isRongyuanCustomLightUiMode(mode: unknown): boolean {
  const value = Number(mode);
  return (
    value === RONGYUAN_UI_CUSTOM_LIGHT_MODE ||
    value === 128 ||
    value === RONGYUAN_PROTOCOL_USER_PICTURE_MODE
  );
}

/**
 * 协议音乐律动 0x14 / 0x16。
 * 十进制 20 与星闪「音乐律动」UI 模式相同，不能原样写进容圆 SET_LED。
 */
export function isRongyuanMusicProtocolMode(mode: unknown): boolean {
  const value = Number(mode);
  return value === 0x14 || value === 0x16;
}

/** 设备 GET_LED mode(0x0D) → UI 灯效按钮值(0x80)；音乐律动不暴露成星闪的 mode 20 */
export function mapRongyuanProtocolModeToUiMode(protocolMode: unknown): number {
  const value = Number(protocolMode);
  if (
    value === RONGYUAN_PROTOCOL_USER_PICTURE_MODE ||
    value === RONGYUAN_UI_CUSTOM_LIGHT_MODE ||
    value === 128
  ) {
    return RONGYUAN_UI_CUSTOM_LIGHT_MODE;
  }
  if (isRongyuanMusicProtocolMode(value)) {
    return 0x01;
  }
  return value;
}

/** 退出律动 / 快照恢复时可用的常规灯效。自定义和音乐律动都收成常亮 */
export function coerceRongyuanRoutineUiMode(mode: unknown): number {
  const value = Number(mode);
  if (
    !Number.isFinite(value) ||
    isRongyuanCustomLightUiMode(value) ||
    isRongyuanMusicProtocolMode(value)
  ) {
    return 0x01;
  }
  return value;
}

export type RongyuanLightingUiOption = {
  label: string;
  value: number;
  original: string;
};

export function getRongyuanLightEffectDisplayName(
  effectId: number
): string | undefined {
  return PROTOCOL_EFFECT_BY_ID.get(effectId)?.displayName;
}

/** UI mode(含 0x80) → 协议 effectId。星闪 mode 20（0x14）不得下发成音乐律动 */
export function resolveRongyuanProtocolEffectId(uiMode: number): number {
  if (isRongyuanCustomLightUiMode(uiMode)) {
    return RONGYUAN_PROTOCOL_USER_PICTURE_MODE;
  }
  if (isRongyuanMusicProtocolMode(uiMode)) {
    return 0x01;
  }
  return uiMode;
}

/** 设备是否处于 USERPIC(0x0D) 图案模式 */
export function isRongyuanUserPictureDeviceMode(mode: unknown): boolean {
  return Number(mode) === RONGYUAN_PROTOCOL_USER_PICTURE_MODE;
}

export type RongyuanLightDirectionOption = {
  id: number;
  label: string;
};

const RONGYUAN_CARDINAL_DIRECTIONS: readonly RongyuanLightDirectionOption[] = [
  { id: 0, label: "向右" },
  { id: 1, label: "向左" },
  { id: 2, label: "向下" },
  { id: 3, label: "向上" },
];

/** 协议 LED Param Table：各灯效 supportedDirections（options 高 4 位） */
const RONGYUAN_EFFECT_DIRECTIONS: Partial<
  Record<number, readonly RongyuanLightDirectionOption[]>
> = {
  0x04: RONGYUAN_CARDINAL_DIRECTIONS,
  0x07: [
    { id: 0, label: "默认" },
    { id: 1, label: "裁剪" },
  ],
  0x0b: [
    { id: 0, label: "向外" },
    { id: 1, label: "向内" },
  ],
  0x0c: [
    { id: 0, label: "向右" },
    { id: 1, label: "向左" },
  ],
  /** 协议层：0x0d direction 为图案索引；UI 仅在「自定义」模式展示图层切换 */
  0x0d: [
    { id: 0, label: "图案0" },
    { id: 1, label: "图案1" },
    { id: 2, label: "图案2" },
    { id: 3, label: "图案3" },
    { id: 4, label: "图案4" },
  ],
  0x0f: [
    { id: 0, label: "顺时针" },
    { id: 1, label: "逆时针" },
  ],
};

/** 自定义逐键配色 UI：五层图案（仅 mode=0x80 时展示，不与「百花争艳」混用） */
export const RONGYUAN_CUSTOM_LAYER_OPTIONS: readonly RongyuanLightDirectionOption[] =
  [
    { id: 0, label: "图层0" },
    { id: 1, label: "图层1" },
    { id: 2, label: "图层2" },
    { id: 3, label: "图层3" },
    { id: 4, label: "图层4" },
  ] as const;

function getRongyuanProtocolDirectionOptions(
  effectId: number
): readonly RongyuanLightDirectionOption[] | null {
  const protocolId = resolveRongyuanProtocolEffectId(effectId);
  return RONGYUAN_EFFECT_DIRECTIONS[protocolId] ?? null;
}

/** 常规灯效方向选项（不含 0x0d/0x80；图层见 RONGYUAN_CUSTOM_LAYER_OPTIONS） */
export function getRongyuanLightEffectDirections(
  effectId: number
): readonly RongyuanLightDirectionOption[] | null {
  const protocolId = resolveRongyuanProtocolEffectId(effectId);
  if (protocolId === 0x0d) return null;
  return RONGYUAN_EFFECT_DIRECTIONS[protocolId] ?? null;
}

export function rongyuanEffectSupportsDirection(effectId: number): boolean {
  return Boolean(getRongyuanLightEffectDirections(effectId)?.length);
}

/** 星闪遗留 Forward/Backward → 峰回路转(0x0f) 方向 id */
function mapLegacyDirectionToRongyuanId(
  effectId: number,
  raw: unknown
): number | undefined {
  if (raw === "Forward") return 0;
  if (raw === "Backward") return 1;
  if (
    typeof raw === "string" &&
    raw.trim() !== "" &&
    Number.isFinite(Number(raw))
  ) {
    return Number(raw);
  }
  if (typeof raw === "number" && Number.isFinite(raw)) return raw;
  return undefined;
}

export function normalizeRongyuanDirectionId(
  effectId: number,
  rawDirection?: unknown
): number {
  const options = getRongyuanProtocolDirectionOptions(effectId);
  if (!options?.length) return 0;
  const legacy = mapLegacyDirectionToRongyuanId(effectId, rawDirection);
  const candidate = legacy ?? 0;
  if (options.some((item) => item.id === candidate)) return candidate;
  return options[0].id;
}

export function getRongyuanDirectionLabel(
  effectId: number,
  directionId: number
): string {
  const protocolId = resolveRongyuanProtocolEffectId(effectId);
  if (protocolId === 0x0d) {
    return (
      RONGYUAN_CUSTOM_LAYER_OPTIONS.find((item) => item.id === directionId)
        ?.label ?? String(directionId)
    );
  }
  const options = getRongyuanProtocolDirectionOptions(effectId);
  return (
    options?.find((item) => item.id === directionId)?.label ??
    String(directionId)
  );
}

/** 背光灯效按钮（官方顺序 + 末尾「自定义」） */
export function buildRongyuanLightingUiOptions(options?: {
  includeCustom?: boolean;
}): RongyuanLightingUiOption[] {
  const includeCustom = options?.includeCustom !== false;

  const list: RongyuanLightingUiOption[] = [];
  for (const effectId of RONGYUAN_UI_LIGHT_EFFECT_ORDER) {
    const meta = PROTOCOL_EFFECT_BY_ID.get(effectId);
    if (!meta?.uiEnabled) continue;
    list.push({
      label: meta.displayName,
      value: effectId,
      original: `0x${effectId.toString(16).padStart(2, "0")}`,
    });
  }

  if (includeCustom) {
    list.push({
      label: "自定义",
      value: RONGYUAN_UI_CUSTOM_LIGHT_MODE,
      original: "0x80",
    });
  }

  return list;
}

export function isRongyuanDecorateOffUiMode(mode: unknown): boolean {
  const value = Number(mode);
  return value === RONGYUAN_DECORATE_OFF_UI_MODE || value === 0;
}

/** UI mode(含 9999) → 灯带协议 effectId */
export function resolveRongyuanDecorateProtocolEffectId(
  uiMode: number
): number {
  if (isRongyuanDecorateOffUiMode(uiMode)) {
    return 0x00;
  }
  return uiMode;
}

/** 设备 GET_SLEDPARAM mode → UI 按钮 value */
export function mapRongyuanDecorateDeviceModeToUiMode(
  deviceMode: unknown
): number {
  const value = Number(deviceMode);
  if (value === 0x00) {
    return RONGYUAN_DECORATE_OFF_UI_MODE;
  }
  return value;
}

/** 灯带 Tab 按钮（官方 6 项：关闭 / 恒亮 / 呼吸 / 霓虹 / 光波 / 跑马） */
export function buildRongyuanDecorateLightingUiOptions(): RongyuanLightingUiOption[] {
  return RONGYUAN_DECORATE_UI_EFFECTS.map((item) => {
    const effectId = item.effectId;
    const uiValue = item.uiValue ?? effectId;
    return {
      label: item.displayName,
      value: uiValue,
      original:
        uiValue === RONGYUAN_DECORATE_OFF_UI_MODE
          ? "off"
          : `0x${effectId.toString(16).padStart(2, "0")}`,
    };
  });
}

/** 容园亮度/速度 UI 刻度：20% 步进，最低 20%，最高 100%（5 档 ↔ 设备 0–4） */
export const RONGYUAN_LIGHT_UI_PERCENT = {
  step: 20,
  min: 20,
  max: 100,
} as const;

const RONGYUAN_DEVICE_LEVEL_MAX = 4;

/** UI 百分比吸附到 20/40/60/80/100 */
export function snapRongyuanUiLightPercent(percent: number): number {
  const { step, min, max } = RONGYUAN_LIGHT_UI_PERCENT;
  const clamped = Math.min(max, Math.max(min, Number(percent) || min));
  return Math.round(clamped / step) * step;
}

/** 设备档位 0–4 → UI 百分比（20/40/60/80/100） */
export function rongyuanDeviceLevelToUiPercent(level: number): number {
  const clamped = Math.min(
    RONGYUAN_DEVICE_LEVEL_MAX,
    Math.max(0, Number(level) || 0)
  );
  return (clamped + 1) * RONGYUAN_LIGHT_UI_PERCENT.step;
}

/** UI 百分比 → 设备亮度档位 0–4（0=最暗，4=最亮） */
export function rongyuanUiPercentToDeviceLevel(percent: number): number {
  const snapped = snapRongyuanUiLightPercent(percent);
  return Math.min(
    RONGYUAN_DEVICE_LEVEL_MAX,
    Math.max(0, snapped / RONGYUAN_LIGHT_UI_PERCENT.step - 1)
  );
}

/** 设备速度档位 0–4 → UI 百分比（协议 0=最快，4=最慢，与亮度方向相反） */
export function rongyuanDeviceSpeedLevelToUiPercent(level: number): number {
  const clamped = Math.min(
    RONGYUAN_DEVICE_LEVEL_MAX,
    Math.max(0, Number(level) || 0)
  );
  return (
    (RONGYUAN_DEVICE_LEVEL_MAX - clamped + 1) * RONGYUAN_LIGHT_UI_PERCENT.step
  );
}

/** UI 百分比 → 设备速度档位 0–4（背光：0=最快，4=最慢） */
export function rongyuanUiPercentToDeviceSpeedLevel(percent: number): number {
  const snapped = snapRongyuanUiLightPercent(percent);
  const uiIndex = snapped / RONGYUAN_LIGHT_UI_PERCENT.step - 1;
  return RONGYUAN_DEVICE_LEVEL_MAX - uiIndex;
}

/** 设备速度档位 → UI 百分比（按灯区；背光反向，灯带与亮度同向） */
export function rongyuanDeviceSpeedLevelToUiPercentForArea(
  level: number,
  area: "Keyboard" | "Decorate1"
): number {
  if (area === "Decorate1") {
    return rongyuanDeviceLevelToUiPercent(level);
  }
  return rongyuanDeviceSpeedLevelToUiPercent(level);
}

/** UI 百分比 → 设备速度档位（按灯区；背光反向，灯带与亮度同向） */
export function rongyuanUiPercentToDeviceSpeedLevelForArea(
  percent: number,
  area: "Keyboard" | "Decorate1"
): number {
  if (area === "Decorate1") {
    return rongyuanUiPercentToDeviceLevel(percent);
  }
  return rongyuanUiPercentToDeviceSpeedLevel(percent);
}

/** 与驱动 UI 一致的 7 档预设色（slot 0 为炫彩，不在此列表） */
export const STANDARD_EFFECT_PALETTE_HEX = [
  "#FF3D3D",
  "#FFD42B",
  "#2EE944",
  "#00D8E8",
  "#2558FF",
  "#E83FD7",
  "#E8E8E8",
] as const;

export const STANDARD_EFFECT_DEFAULT_HEX = STANDARD_EFFECT_PALETTE_HEX[0];

function hexToRgbChannels(hex: string): { R: number; G: number; B: number } {
  let normalized = hex.replace("#", "");
  if (normalized.length === 3) {
    normalized = normalized
      .split("")
      .map((char) => char + char)
      .join("");
  }
  return {
    R: parseInt(normalized.slice(0, 2), 16),
    G: parseInt(normalized.slice(2, 4), 16),
    B: parseInt(normalized.slice(4, 6), 16),
  };
}

/** Palette API：index 0 占位，1–7 为预设色 */
export function buildStandardEffectStaticColors(): Array<{
  R: number;
  G: number;
  B: number;
  H: number;
}> {
  return [
    { R: 0, G: 0, B: 0, H: 0 },
    ...STANDARD_EFFECT_PALETTE_HEX.map((hex) => ({
      ...hexToRgbChannels(hex),
      H: 255,
    })),
  ];
}

export function inferStandardEffectPaletteIndex(
  color: { R?: number; G?: number; B?: number },
  colorMode?: unknown
): number {
  if (
    colorMode === "Multi" ||
    colorMode === "multi" ||
    colorMode === 1 ||
    colorMode === "1"
  ) {
    return 0;
  }
  const r = Number(color.R ?? 0);
  const g = Number(color.G ?? 0);
  const b = Number(color.B ?? 0);
  for (let i = 0; i < STANDARD_EFFECT_PALETTE_HEX.length; i++) {
    const slot = hexToRgbChannels(STANDARD_EFFECT_PALETTE_HEX[i]);
    if (slot.R === r && slot.G === g && slot.B === b) {
      return i + 1;
    }
  }
  return 1;
}

/** 容园官方默认：亮度 100%，速度 60% */
export const RONGYUAN_LIGHT_UI_DEFAULTS = {
  luminance: RONGYUAN_LIGHT_UI_PERCENT.max,
  speed: 60,
} as const;

/** 与 UI 默认对应的设备档位（brightness=4, speed=2） */
export const RONGYUAN_LIGHT_DEVICE_DEFAULTS = {
  brightness: rongyuanUiPercentToDeviceLevel(
    RONGYUAN_LIGHT_UI_DEFAULTS.luminance
  ),
  speed: rongyuanUiPercentToDeviceSpeedLevel(RONGYUAN_LIGHT_UI_DEFAULTS.speed),
} as const;
